import { ComposicoesRelatorioComposicaoContas } from "core/features/relatorios/conta-com-composicoes/typings";
import { formatMoney } from "infra/helpers/number";
import { useMemo } from "react";
import { PDFTableColumn } from "components/PDF/Table";
import { View, Text } from "@react-pdf/renderer";
import moment from "moment";
import { OperacaoEnum } from "resources/enums/conta";
import { RelatorioContasComComposicaoFields } from "core/features/relatorios/typings";
import { mapFormaPagamentoToLabel } from "infra/helpers/mapFormaPagamentoToLabel";
import colors from "resources/theme/colors";

function useRelatorioComposicaoContasPdfColumns(
  operacao: OperacaoEnum,
  fields?: RelatorioContasComComposicaoFields[],
): PDFTableColumn<ComposicoesRelatorioComposicaoContas>[] {
  return useMemo(() => {
    const columns: PDFTableColumn<ComposicoesRelatorioComposicaoContas>[] = [];

    if (fields?.includes("categoria")) {
      columns.push({
        title: "Categoria",
        flexGrow: 2,
        render: (original) => (
          <View>
            <Text>
              {original?.categoriaFinanceira?.breadcrumb}{" "}
              {original?.categoriaFinanceira?.nome}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("complemento")) {
      columns.push({
        title: "Complemento",
        flexGrow: 0.9,
        render: (original) => (
          <View>
            <Text>{original?.complemento ?? "--"}</Text>
          </View>
        ),
      });
    }

    if (fields?.includes("dataCriacao")) {
      columns.push({
        title: "Geração",
        flexGrow: 0.9,
        textAlign: "center",
        render: (original) => (
          <View>
            <Text>
              {original.dataCriacao && moment(original.dataCriacao).isValid()
                ? moment(original.dataCriacao).format("DD/MM/YYYY")
                : "--"}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("competencia")) {
      columns.push({
        title: "Competência",
        flexGrow: 0.9,
        textAlign: "center",
        render: (original) => (
          <View>
            <Text>
              {original.dataCompetencia &&
              moment(original.dataCompetencia, "YYYY-MM").isValid()
                ? moment(original.dataCompetencia, "YYYY-MM").format("MM/YYYY")
                : "--"}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("dataVencimento")) {
      columns.push({
        title: "Vencimento",
        flexGrow: 0.8,
        textAlign: "center",
        render: (original) => (
          <View>
            <Text>
              {original.dataVencimento
                ? moment(original.dataVencimento).format("DD/MM/YYYY")
                : "--"}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("dataPagamento")) {
      columns.push({
        title: "Liquidação",
        flexGrow: 0.7,
        textAlign: "center",
        render: (original) => (
          <View style={{ flexDirection: "column", gap: 1 }}>
            <Text>
              {original.dataPagamento
                ? moment(original.dataPagamento).format("DD/MM/YYYY")
                : "--"}
            </Text>
            {fields?.includes("formaPagamento") ? (
              <Text style={{ fontWeight: "medium", color: colors.silver }}>
                {original.formaPagamentoId
                  ? mapFormaPagamentoToLabel(original.formaPagamentoId)
                  : ""}
              </Text>
            ) : null}
          </View>
        ),
      });
    }

    if (fields?.includes("dataMovimentacao")) {
      columns.push({
        title: "Crédito",
        flexGrow: 0.7,
        textAlign: "center",
        render: (original) => (
          <View>
            <Text>
              {original.dataMovimentacao
                ? moment(original.dataMovimentacao).format("DD/MM/YYYY")
                : "--"}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("centroDeCusto")) {
      columns.push({
        title: "Centro de custo",
        flexGrow: 1,
        render: (original) => (
          <View>
            <Text>{original?.centroDeCusto?.nome ?? "--"}</Text>
          </View>
        ),
      });
    }

    if (fields?.includes("valor")) {
      columns.push({
        title: "Valor",
        flexGrow: 1,
        textAlign: "right",
        paddingHorizontal: 10,
        render: (original) => (
          <View>
            <Text>
              {formatMoney(
                original.valor *
                  original.categoriaFinanceira.operacao *
                  operacao,
              )}
            </Text>
          </View>
        ),
      });
    }

    if (fields?.includes("tenantConta")) {
      columns.push({
        title: "Conta bancária",
        flexGrow: 1.25,
        textAlign: "left",
        render: (original) => (
          <View>
            <Text>{original.tenantConta?.nome ?? ""}</Text>
          </View>
        ),
      });
    }

    if (fields?.includes("descricao")) {
      columns.push({
        title: "Descrição",
        flexGrow: 2,
        textAlign: "left",
        render: (original) => (
          <View>
            <Text>{original.descricao ?? ""}</Text>
          </View>
        ),
      });
    }

    if (fields?.includes("nossoNumero")) {
      columns.push({
        title: "Nosso número",
        flexGrow: 1,
        textAlign: "left",
        render: (original) => (
          <View>
            <Text>{original.nossoNumero ?? ""}</Text>
          </View>
        ),
      });
    }

    if (columns.length) {
      columns[columns.length - 1].textAlign = "right";
    }

    return columns;
  }, [operacao, fields]);
}

export default useRelatorioComposicaoContasPdfColumns;
